import React from "react";

import { Box, Typography } from "@mui/material";
import { Navigate, RouteObject, useParams } from "react-router";

// Application / System pages
import AdoptStackPage from "src/domains/application/pages/AdoptStackPage";
import ConnectionsPage from "src/domains/application/pages/ConnectionsPage";
import GitHubCallbackPage from "src/domains/application/pages/GitHubCallbackPage";
import ReviewServicesPage from "src/domains/application/pages/ReviewServicesPage";
import StackDetailsPage from "src/domains/application/pages/StackDetailsPage";
import StackEditPage from "src/domains/application/pages/StackEditPage";
import { LoginPage, RegisterPage } from "src/domains/auth/pages";
import {
  OrgScopedRoute,
  ProtectedRoute,
  PublicRoute,
} from "src/domains/auth/utils/routeUtils";
// Organization pages
import ErrorPage from "src/domains/error/pages/ErrorPage";
import InitializationLoading from "src/domains/loading/pages/InitializationLoading";
import {
  OrganizationList as OrganizationsPage,
  OrganizationDetails as OrganizationDetailsPage,
} from "src/domains/organization/pages";
import CreateOrganization from "src/domains/organization/pages/create";
import PlatformProductDetailsPage from "src/domains/platform-products/pages/PlatformProductDetailsPage";
import PlatformProductsPage from "src/domains/platform-products/pages/PlatformProductsPage";
// Project pages
import { ProjectsList as ProjectsPage } from "src/domains/project/pages";
import ProjectDetails from "src/domains/project/pages";
import CreateProjectPage from "src/domains/project/pages/create";
import RecoveryPage from "src/domains/recovery/pages/RecoveryPage";
import { ProvisionResourcePage } from "src/domains/resource/pages/ProvisionResourcePage";
import { ResourceDetailPage } from "src/domains/resource/pages/ResourceDetailPage";
// User pages
import UserProfile from "src/domains/user/pages";
import UserSettings from "src/domains/user/pages/settings";
import {
  LOADING,
  RECOVERY,
  ERROR,
  AUTH,
  LEGACY_DASHBOARD,
  ORGANIZATIONS,
  PROJECTS,
  USER,
  ACCOUNT,
  SETTINGS,
  PLATFORM_PRODUCTS,
  STACKS,
  GITHUB,
} from "src/platform/constants/routes";
import DashboardLayout from "src/shell/layout/DashboardLayout";

const OrgMembersPlaceholder: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4">Organization Members</Typography>
  </Box>
);

const OrgSettingsPlaceholder: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4">Organization Settings</Typography>
  </Box>
);

/**
 * Redirect for the legacy Create Stack wizard route.
 * The 7-step wizard was replaced by the CreateApplicationDrawer on the
 * project page. Redirect to the project page with `?drawer=app` so the
 * drawer auto-opens.
 */
const CreateStackRedirect: React.FC = () => {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const target = PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias ?? "");
  return <Navigate to={`${target}?drawer=app`} replace />;
};

/**
 * Redirect for the legacy Stacks list page.
 * The standalone list page was replaced by inline application display on
 * the project details page. Redirect to the project page.
 */
const StacksListRedirect: React.FC = () => {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const target = PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias ?? "");
  return <Navigate to={target} replace />;
};

/**
 * State Machine Phase Routes - Public access during initialization/recovery
 */
const stateMachineRoutes: RouteObject[] = [
  {
    path: LOADING.path,
    element: <InitializationLoading />,
  },
  {
    path: RECOVERY.path,
    element: <RecoveryPage />,
  },
  {
    path: ERROR.path,
    element: <ErrorPage />,
  },
];

/**
 * Auth routes - public access
 */
const authRoutes: RouteObject[] = [
  {
    path: AUTH.ROOT.path,
    element: <PublicRoute restricted={true} />,
    children: [
      {
        path: AUTH.LOGIN.path,
        element: <LoginPage />,
      },
      {
        path: AUTH.REGISTER.path,
        element: <RegisterPage />,
      },
      // Root redirect to login
      {
        path: AUTH.ROOT.path,
        element: <Navigate to={AUTH.LOGIN.path} replace />,
      },
    ],
  },
];

/**
 * Main application routes (authenticated)
 */
const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // Deprecated dashboard compatibility route. Organization Details is the
      // canonical post-selection landing surface.
      {
        path: LEGACY_DASHBOARD.path,
        element: <Navigate to={ORGANIZATIONS.ROOT.path} replace />,
      },

      // Organizations — list + create
      {
        path: ORGANIZATIONS.ROOT.path,
        element: <OrganizationsPage />,
      },
      {
        path: ORGANIZATIONS.CREATE.path,
        element: <CreateOrganization />,
      },

      // Organization — detail + edit (same component, detects mode via URL)
      {
        path: ORGANIZATIONS.DETAILS.path,
        element: <OrganizationDetailsPage />,
      },
      {
        path: ORGANIZATIONS.EDIT.path,
        element: <OrganizationDetailsPage />,
      },

      // Organization sub-resources
      {
        path: PLATFORM_PRODUCTS.CATALOG.path,
        element: (
          <OrgScopedRoute>
            <PlatformProductsPage />
          </OrgScopedRoute>
        ),
      },
      {
        path: PLATFORM_PRODUCTS.DETAILS.path,
        element: (
          <OrgScopedRoute>
            <PlatformProductDetailsPage />
          </OrgScopedRoute>
        ),
      },
      {
        path: ORGANIZATIONS.MEMBERS.path,
        element: <OrgMembersPlaceholder />,
      },
      {
        path: ORGANIZATIONS.ORG_SETTINGS.path,
        element: <OrgSettingsPlaceholder />,
      },
      {
        path: ORGANIZATIONS.VCS_CONNECTIONS.path,
        element: <ConnectionsPage />,
      },

      // Projects - Organization context
      {
        path: PROJECTS.LIST.path,
        element: <ProjectsPage />,
      },
      {
        path: PROJECTS.CREATE.path,
        element: <CreateProjectPage />,
      },
      {
        path: PROJECTS.DETAILS.path,
        element: (
          <OrgScopedRoute>
            <ProjectDetails />
          </OrgScopedRoute>
        ),
      },
      {
        path: PROJECTS.EDIT.path,
        element: (
          <OrgScopedRoute>
            <ProjectDetails />
          </OrgScopedRoute>
        ),
      },
      {
        path: PROJECTS.RESOURCES_NEW.path,
        element: (
          <OrgScopedRoute>
            <ProvisionResourcePage />
          </OrgScopedRoute>
        ),
      },
      {
        path: PROJECTS.RESOURCES_DETAIL.path,
        element: (
          <OrgScopedRoute>
            <ResourceDetailPage />
          </OrgScopedRoute>
        ),
      },

      // Account — canonical user profile, settings, and billing
      {
        path: ACCOUNT.PROFILE.path,
        element: <UserProfile />,
      },
      {
        path: ACCOUNT.SETTINGS.path,
        element: <UserSettings />,
      },
      // Legacy user paths — redirect to /account/*
      {
        path: USER.PROFILE.path,
        element: <Navigate to={ACCOUNT.PROFILE.path} replace />,
      },
      {
        path: USER.SETTINGS.path,
        element: <Navigate to={ACCOUNT.SETTINGS.path} replace />,
      },

      // Legacy /settings — redirect to /account/settings
      {
        path: SETTINGS.path,
        element: <Navigate to={ACCOUNT.SETTINGS.path} replace />,
      },

      // Stacks (infrastructure stacks, backed by /api/v1/applications)
      // Legacy stacks list page — replaced by inline display on project page
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/stacks",
        element: (
          <OrgScopedRoute>
            <StacksListRedirect />
          </OrgScopedRoute>
        ),
      },
      // Legacy wizard route — replaced by CreateApplicationDrawer on project page
      {
        path: STACKS.CREATE.path,
        element: (
          <OrgScopedRoute>
            <CreateStackRedirect />
          </OrgScopedRoute>
        ),
      },
      // Adopt existing repo — AI inspection + rusha.yml generation
      {
        path: STACKS.ADOPT.path,
        element: (
          <OrgScopedRoute>
            <AdoptStackPage />
          </OrgScopedRoute>
        ),
      },
      {
        path: STACKS.DETAILS.path,
        element: (
          <OrgScopedRoute>
            <StackDetailsPage />
          </OrgScopedRoute>
        ),
      },
      {
        path: STACKS.EDIT.path,
        element: (
          <OrgScopedRoute>
            <StackEditPage />
          </OrgScopedRoute>
        ),
      },
      {
        path: STACKS.SERVICES.path,
        element: (
          <OrgScopedRoute>
            <ReviewServicesPage />
          </OrgScopedRoute>
        ),
      },

      // Backward-compatible redirect: /systems/* → /stacks/*
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems",
        element: (
          <OrgScopedRoute>
            <Navigate
              to="/organizations/:orgAlias/projects/:projectAlias/stacks"
              replace
            />
          </OrgScopedRoute>
        ),
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/:sysAlias",
        element: (
          <OrgScopedRoute>
            <Navigate
              to="/organizations/:orgAlias/projects/:projectAlias/stacks/:sysAlias"
              replace
            />
          </OrgScopedRoute>
        ),
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/create",
        element: (
          <OrgScopedRoute>
            <CreateStackRedirect />
          </OrgScopedRoute>
        ),
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/:sysAlias/edit",
        element: (
          <OrgScopedRoute>
            <Navigate
              to="/organizations/:orgAlias/projects/:projectAlias/stacks/:sysAlias/edit"
              replace
            />
          </OrgScopedRoute>
        ),
      },

      // Root redirect — send to organization selection so the user can
      // establish or change the organization-scoped session.
      {
        path: "/",
        element: <Navigate to={ORGANIZATIONS.ROOT.path} replace />,
      },

      // Fallback for unmatched paths (404 handling)
      {
        path: "*",
        element: <Navigate to={ORGANIZATIONS.ROOT.path} replace />,
      },
    ],
  },
];

/**
 * Combined routes configuration for the application
 * Order matters: state machine routes first, then auth, then app routes
 */
export const routes: RouteObject[] = [
  // State machine phase routes (public access)
  ...stateMachineRoutes,

  // Auth routes (public access)
  ...authRoutes,

  // GitHub App callback route (public access)
  {
    path: GITHUB.CALLBACK.path,
    element: <GitHubCallbackPage />,
  },

  // Main application routes (protected access)
  ...appRoutes,

  // Global redirect to loading (start state machine)
  {
    path: "*",
    element: <Navigate to={LOADING.path} replace />,
  },
];

export default routes;
